/**
 * Domain layer mirror of the canonical praxis spec hierarchy.
 *
 * Pure module: no Convex, no React, no external libs. All exported
 * functions take a single `input` object and destructure.
 *
 * Cross-row references are plain strings — the domain MUST NOT import
 * `Id<...>` from convex.
 */

// ─── Enums ───────────────────────────────────────────────────────────

export const SPEC_LIFECYCLES = ["inactive", "active"] as const;
export type SpecLifecycle = (typeof SPEC_LIFECYCLES)[number];

export const TRIGGERS_CONVENTION_VERSION =
  "praxis.spec-triggers/v1" as const;

// ─── Value objects ───────────────────────────────────────────────────

export interface SpecValueObject {
  readonly _id: string;
  readonly userId: string;
  readonly title: string;
  readonly lifecycle: SpecLifecycle;
  readonly currentVersionId: string | undefined;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface SpecVersionProvenance {
  readonly sourceDocRef: string | undefined;
  readonly extractedAt: number;
  readonly agentModel: string | undefined;
  readonly refinementTurns: number;
}

export interface SpecVersionValueObject {
  readonly _id: string;
  readonly userId: string;
  readonly specId: string;
  readonly versionNumber: number;
  readonly gitSha: string | undefined;
  readonly markdown: string;
  readonly triggers: ReadonlyArray<Record<string, unknown>>;
  readonly triggersPresent: boolean;
  readonly triggersConventionVersion: string;
  readonly provenance: SpecVersionProvenance;
  readonly createdAt: number;
}

export interface SpecNodeValueObject {
  readonly _id: string;
  readonly specVersionId: string;
  readonly nodeId: string;
  readonly kind: string;
  readonly label: string;
  readonly body: string | undefined;
}

export interface SpecEdgeValueObject {
  readonly _id: string;
  readonly specVersionId: string;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly metadata: Record<string, unknown> | undefined;
}

// ─── Pure validators ─────────────────────────────────────────────────

export function isSpecLifecycle(value: string): value is SpecLifecycle {
  return (SPEC_LIFECYCLES as ReadonlyArray<string>).includes(value);
}

// ─── Pure helpers ────────────────────────────────────────────────────

export function assertEdgeReferentialIntegrity(input: {
  readonly nodes: ReadonlyArray<{ readonly nodeId: string }>;
  readonly edges: ReadonlyArray<{
    readonly fromNodeId: string;
    readonly toNodeId: string;
  }>;
}): void {
  const { nodes, edges } = input;
  const nodeIds = new Set<string>();
  for (const node of nodes) nodeIds.add(node.nodeId);
  for (const edge of edges) {
    if (!nodeIds.has(edge.fromNodeId)) {
      throw new Error(
        `spec edge references unknown fromNodeId "${edge.fromNodeId}" (toNodeId="${edge.toNodeId}")`,
      );
    }
    if (!nodeIds.has(edge.toNodeId)) {
      throw new Error(
        `spec edge references unknown toNodeId "${edge.toNodeId}" (fromNodeId="${edge.fromNodeId}")`,
      );
    }
  }
}

// ─── Spec triggers (local mirror of praxis.spec-triggers/v1) ────────
//
// Structural mirror of `packages/praxis/src/lib/spec-triggers.ts` and
// `packages/praxis/src/file/spec-triggers-parser.ts`. Praxis is the
// canonical schema source; clawcraft does NOT import it (praxis-doctrine
// §2.1 — no @soulbound-labs/praxis line in any apps/clawcraft package.json).
// The local parser supports v1 only: a fenced ```yaml block of trigger
// objects with a closed `channel` enum and a non-empty `description`,
// passing through any additional keys.

export const TRIGGER_CHANNELS = ["email", "web_chat", "webhook"] as const;
export type TriggerChannel = (typeof TRIGGER_CHANNELS)[number];

export interface SpecTrigger {
  readonly channel: TriggerChannel;
  readonly description: string;
  readonly [extra: string]: unknown;
}

export interface ParsedSpecTriggers {
  readonly triggers: ReadonlyArray<SpecTrigger>;
  readonly present: boolean;
}

export class SpecTriggersError extends Error {
  public readonly reason: string;
  public readonly line: number | undefined;

  constructor(reason: string, line?: number) {
    super(line === undefined ? reason : `${reason} (line ${line})`);
    this.name = "SpecTriggersError";
    this.reason = reason;
    this.line = line;
  }
}

const TRIGGERS_HEADING = "## Triggers";
const FENCE_OPEN = "```yaml";
const FENCE_CLOSE = "```";

function isTriggerChannel(value: string): value is TriggerChannel {
  return (TRIGGER_CHANNELS as ReadonlyArray<string>).includes(value);
}

/**
 * Parse the `## Triggers` block of a spec markdown document.
 *
 * - Missing heading → `{ triggers: [], present: false }`.
 * - Empty / `[]` body → `{ triggers: [], present: true }`.
 * - Otherwise: items must be a sequence of `- channel: …\n  description: …`
 *   with optional additional `key: value` lines preserved as passthrough.
 */
export function parseSpecTriggers(specMarkdown: string): ParsedSpecTriggers {
  const lines = specMarkdown.replace(/\r\n?/g, "\n").split("\n");

  const headingLine = lines.findIndex(
    (line) => line.trimEnd() === TRIGGERS_HEADING,
  );
  if (headingLine === -1) {
    return { triggers: [], present: false };
  }

  let cursor = headingLine + 1;
  while (cursor < lines.length && lines[cursor]?.trim() === "") cursor += 1;
  if (cursor >= lines.length || lines[cursor]?.trimEnd() !== FENCE_OPEN) {
    throw new SpecTriggersError(
      `'${TRIGGERS_HEADING}' heading found but no \`\`\`yaml fenced block follows`,
      headingLine + 1,
    );
  }
  const openFenceLine = cursor;

  let closeFenceLine = -1;
  for (let i = openFenceLine + 1; i < lines.length; i += 1) {
    if (lines[i]?.trimEnd() === FENCE_CLOSE) {
      closeFenceLine = i;
      break;
    }
  }
  if (closeFenceLine === -1) {
    throw new SpecTriggersError(
      `'${TRIGGERS_HEADING}' yaml block is missing a closing \`\`\` fence`,
      openFenceLine + 1,
    );
  }

  const body = lines
    .slice(openFenceLine + 1, closeFenceLine)
    .join("\n")
    .trim();
  if (body === "" || body === "[]") {
    return { triggers: [], present: true };
  }

  const triggers = parseTriggerItems(body, openFenceLine + 1);
  return { triggers, present: true };
}

function parseTriggerItems(body: string, baseLine: number): SpecTrigger[] {
  const lines = body.split("\n");
  const items: Array<Record<string, unknown>> = [];
  let current: Record<string, unknown> | null = null;

  for (let i = 0; i < lines.length; i += 1) {
    const raw = lines[i] ?? "";
    if (raw.trim() === "") continue;

    if (raw.startsWith("- ")) {
      current = {};
      items.push(current);
      const after = raw.slice(2).trimStart();
      assignKeyValue(after, current, baseLine + i);
      continue;
    }

    if (current === null) {
      throw new SpecTriggersError(
        "yaml body must start with a `- ` list item",
        baseLine + i,
      );
    }

    if (raw.startsWith("  ") || raw.startsWith("\t")) {
      assignKeyValue(raw.trimStart(), current, baseLine + i);
      continue;
    }

    throw new SpecTriggersError(
      `unexpected non-list line in triggers yaml: "${raw}"`,
      baseLine + i,
    );
  }

  const result: SpecTrigger[] = [];
  for (let idx = 0; idx < items.length; idx += 1) {
    const item = items[idx]!;
    const channel = item.channel;
    const description = item.description;
    if (typeof channel !== "string" || !isTriggerChannel(channel)) {
      throw new SpecTriggersError(
        `trigger[${idx}].channel must be one of ${TRIGGER_CHANNELS.join(", ")}; got ${JSON.stringify(channel)}`,
      );
    }
    if (typeof description !== "string" || description.length === 0) {
      throw new SpecTriggersError(
        `trigger[${idx}].description must be a non-empty string`,
      );
    }
    result.push({ ...item, channel, description } as SpecTrigger);
  }
  return result;
}

function assignKeyValue(
  fragment: string,
  target: Record<string, unknown>,
  line: number,
): void {
  const colon = fragment.indexOf(":");
  if (colon === -1) {
    throw new SpecTriggersError(
      `expected "key: value" in triggers yaml, got "${fragment}"`,
      line,
    );
  }
  const key = fragment.slice(0, colon).trim();
  if (key === "") {
    throw new SpecTriggersError(
      "triggers yaml key must be non-empty",
      line,
    );
  }
  const rawValue = fragment.slice(colon + 1).trim();
  target[key] = unquoteScalar(rawValue);
}

function unquoteScalar(value: string): unknown {
  if (value === "") return "";
  if (value === "true") return true;
  if (value === "false") return false;
  if (value === "null" || value === "~") return null;
  if (/^-?\d+$/.test(value)) return Number.parseInt(value, 10);
  if (/^-?\d+\.\d+$/.test(value)) return Number.parseFloat(value);
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

// ─── Display constants ───────────────────────────────────────────────

export const SPEC_LIFECYCLE_DISPLAY_LABEL: Readonly<
  Record<SpecLifecycle, string>
> = {
  inactive: "Inactive",
  active: "Active",
};

export type SpecActivityLabel = "Live" | "Paused" | "Needs check";

/**
 * Human-facing three-state activity pill (workflow-pause-toggle spec).
 *
 * Distinct axis from {@link SPEC_LIFECYCLE_DISPLAY_LABEL} (the `lifecycle`
 * enum). Two independent booleans compose:
 *   - `userPaused` (praxis-owned pause bit) wins: → "Paused".
 *   - else `active === true` (validation-derived) → "Live".
 *   - else → "Needs check" (unvalidated — Check-it unlocks publishing).
 */
export function deriveSpecActivityLabel({
  active,
  userPaused,
}: {
  active: boolean | undefined;
  userPaused?: boolean | undefined;
}): SpecActivityLabel {
  if (userPaused === true) return "Paused";
  return active === true ? "Live" : "Needs check";
}

// ─── Workflow URL slugs ──────────────────────────────────────────────
//
// The dedicated workflows detail route addresses a spec by a vanity slug:
// `<kebab(title)>~<specId>`. Resolution keys ONLY on the id after the LAST
// `~` — the title stem is presentation, so a stale link (renamed workflow)
// still resolves and the route canonical-redirects. No stored slug column
// exists (projection is the sole `specs` writer, praxis-doctrine §5.2);
// the slug is derived at render time from `specs.title` + `specs._id`.
// `~` is URL-unreserved (RFC 3986) and can never appear in a Convex id
// (lowercase alphanumerics), so the split is unambiguous.

const SLUG_STEM_MAX_LENGTH = 64;

/** Kebab-case a workflow title into a URL-safe slug stem (may be empty). */
function kebabCaseSlugStem(title: string): string {
  return title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_STEM_MAX_LENGTH)
    .replace(/-+$/, "");
}

/**
 * Build the canonical workflows-detail slug for a spec. Titles that kebab
 * to nothing (empty, all-symbol) yield the bare id — `parseSpecIdFromSlug`
 * accepts both shapes.
 */
export function buildSpecSlug({
  title,
  specId,
}: {
  title: string;
  specId: string;
}): string {
  const stem = kebabCaseSlugStem(title);
  return stem === "" ? specId : `${stem}~${specId}`;
}

/**
 * Extract the spec id from a workflows-detail slug: the token after the
 * LAST `~`, or the whole slug when no `~` is present (bare-id shape).
 * Returns the raw token — the caller validates it (`ctx.db.normalizeId`
 * server-side → `null` on garbage, which the route renders as not-found).
 */
export function parseSpecIdFromSlug({ slug }: { slug: string }): string {
  const separator = slug.lastIndexOf("~");
  return separator === -1 ? slug : slug.slice(separator + 1);
}
